import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMediaQuery } from "react-responsive";
import { useParams } from "react-router-dom";
import axiosInstance from "../../services/axiosInstance";
import { Card, List, Spin, Table, Button, message, Empty, Progress, Tag } from "antd";
import Title from "antd/es/typography/Title";
import { catchError } from "../../utils/errorHandler";
import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import SpinLoading from "./SpinLoading";
import AssignSubscriber from "./AssignSubscriber";
import moment from "moment";
import dayjs from "dayjs";

// import { Modal, Form, DatePicker } from 'antd';

import { Popover, DatePicker, Input } from "antd";

const MobileViewComponent = ({
  data,
  onDelete,
  deletingRow,
  renderPopoverContent,
  permissions,
  openPopoverUuid,
  onPopoverOpenChange,
}) => {
  const columns = [
    {
      dataIndex: "key",
      key: "key",
      render: (text) => <strong>{text}</strong>,
    },
    {
      dataIndex: "value",
      key: "value",
      render: (text) => (
        <span
          style={{
            wordWrap: "break-word",
            wordBreak: "break-word",
            whiteSpace: "normal",
          }}>
          {text || "N/A"}
        </span>
      ),
    },
  ];

  const transformDataForTable = (item) => [
    { key: "Type", value: item.type },
    { key: "Name", value: item.subscriber },
    { key: "Word Count", value: item.no_of_word },
    {
      key: "Completion Date",
      value: item.date_of_completion
        ? moment(item.date_of_completion).local().format("DD-MM-YYYY hh:mm A")
        : "N/A",
    },
    {
      key: "Deadline",
      value: (
        <Popover
          content={renderPopoverContent(item)}
          title="Edit Deadline"
          trigger="click"
          open={openPopoverUuid === item.uuid}
          onOpenChange={(visible) => onPopoverOpenChange(visible, item)}>
          {item.deadline
            ? moment(item.deadline).local().format("DD-MM-YYYY hh:mm A")
            : "N/A"}{" "}
          {permissions.includes("can_edit_subscriber_deadline") && (
            <Button
              type="link"
              style={{ background: "transparent" }}
              icon={<EditOutlined />}
            />
          )}
        </Popover>
      ),
    },

    { key: "Author", value: item.author },
    {
      key: "Created	",
      value: item.created
        ? moment(item.created).local().format("DD-MM-YYYY hh:mm A")
        : "N/A",
    },
    {
      key: "Delete",
      value: (
        <Button
          type="link"
          onClick={() => item?.can_delete && onDelete(item.uuid)}
          disabled={!item?.can_delete}
          style={{ padding: 0 }}
        >
          {deletingRow === item.uuid ? (
            <SpinLoading />
          ) : (
            <DeleteOutlined style={{ color: item?.can_delete ? "red" : "#ccc", fontSize: "20px" }} />
          )}
        </Button>
      ),
    },
  ];

  return (
    <div style={{ backgroundColor: "#f2f6f9", padding: "10px" }}>
      {data && data.length > 0 ? (
        data.map((item, index) => (
          <Table
            key={index}
            columns={columns}
            dataSource={transformDataForTable(item)}
            pagination={false}
            bordered
            size="small"
            style={{ marginBottom: "25px", borderRadius: "5px" }}
            showHeader={false}
          />
        ))
      ) : (
        <Empty description="No data available" />
      )}
    </div>
  );
};

// "Test Lead 01 (Team Lead 62218)" -> "Team Lead"; "Team Lead 62218" (masked, no name) -> "Team Lead"
const extractRole = (subscriberStr) => {
  if (!subscriberStr) return "Unassigned";
  const parenMatch = subscriberStr.match(/\(([^)]+)\)\s*$/);
  const inner = parenMatch ? parenMatch[1] : subscriberStr;
  const role = inner.replace(/\s*\d+\s*$/, "").trim();
  return role || "Unassigned";
};

const renderBar = (assigned, total) => {
  const percent = total > 0 ? Math.min(100, Math.round((assigned / total) * 100)) : 0;
  return (
    <Progress
      percent={percent}
      success={{ percent }}
      trailColor="#f5222d"
      showInfo={false}
      style={{ marginBottom: 4 }}
    />
  );
};

const WordCountSummary = ({ dataSource, totalWords }) => {
  const { assignedWords, roleBreakdown } = useMemo(() => {
    let assigned = 0;
    const roleMap = {};
    dataSource.forEach((item) => {
      const words = Number(item.no_of_word) || 0;
      assigned += words;
      const role = extractRole(item.subscriber);
      roleMap[role] = (roleMap[role] || 0) + words;
    });
    return { assignedWords: assigned, roleBreakdown: roleMap };
  }, [dataSource]);

  const leftWords = totalWords - assignedWords;

  return (
    <Card size="small" style={{ marginBottom: 16, background: "#fafafa" }}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
          alignItems: "center",
          marginBottom: 6,
        }}>
        <span>
          Total Word Count: <strong>{totalWords}</strong>
        </span>
        <Tag color="red">Left: {leftWords}</Tag>
      </div>

      {Object.keys(roleBreakdown).length > 0 && (
        <div style={{ paddingLeft: 8, borderLeft: "2px solid #d9d9d9", marginTop: 10 }}>
          {Object.entries(roleBreakdown).map(([role, words]) => {
            const roleLeft = totalWords - words;
            return (
              <div key={role} style={{ paddingLeft: 12, marginBottom: 8 }}>
                <div style={{ lineHeight: "22px" }}>
                  <span style={{ color: "#888" }}>└ </span>
                  <strong>{role}</strong>
                  {" — "}
                  <Tag color="green" style={{ marginInlineEnd: 4 }}>
                    Assigned: {words}
                  </Tag>
                  <Tag color="red">Left: {roleLeft}</Tag>
                </div>
                {totalWords > 0 && (
                  <div style={{ paddingLeft: 20 }}>{renderBar(words, totalWords)}</div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};

const ManageSubscriber = ({ permissions, orderData, fetchOrderDetails, setRenderStatic }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [updatedDeadlines, setUpdatedDeadlines] = useState({});
  const [selectedDate, setSelectedDate] = useState();
  const [updatingDeadline, setUpdatingDeadline] = useState(false);
  const [openPopoverUuid, setOpenPopoverUuid] = useState(null);
  // Tracks which row's deadline is currently loaded into `selectedDate`,
  // independent of Popover visibility — see handlePopoverOpenChange.
  const loadedDeadlineForUuid = useRef(null);

  const { orderKey } = useParams();
  const [dataSource, setDataSource] = useState([]);
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
  const [deletingRow, setDeletingRow] = useState(null);
  const [assignSubscribe, setShowAssign] = useState(false);
  const [formSubmit, setFormSubmit] = useState(false);

  const handleDeadlineChange = (date, record) => {
    setSelectedDate(date);
  };

  // The DatePicker's calendar panel is a floating overlay outside the
  // Popover's own DOM subtree, so picking a day can register as a "click
  // outside" and fire onOpenChange(false)+onOpenChange(true) again even
  // though the user is still mid-edit on the same row. `loadedDeadlineForUuid`
  // is only updated here and after a successful submit — never by a close
  // event — so that open/close blips on the same row don't re-load the old
  // deadline and wipe out the date the user just picked before Submit reads it.
  const handlePopoverOpenChange = (visible, record) => {
    if (visible) {
      if (loadedDeadlineForUuid.current !== record.uuid) {
        setSelectedDate(record.deadline ? dayjs(record.deadline) : null);
        loadedDeadlineForUuid.current = record.uuid;
      }
      setOpenPopoverUuid(record.uuid);
    } else if (openPopoverUuid === record.uuid) {
      setOpenPopoverUuid(null);
    }
  };

  const fetchSubscribersList = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(
        `task/v2/task-subscribers-list/${orderKey}/`
      );
      const data = res?.data?.data || [];
      setDataSource(data);
    } catch (err) {
      console.log("Error fetching subscribers list", err);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDeadline = async (record) => {
    if (!selectedDate) {
      message.warning("Please select a date.");
      return;
    }

    try {
      setUpdatingDeadline(true);
      const response = await axiosInstance.post(
        `task/v2/edit/subscriber-deadline/${record.uuid}/`,
        {
          updated_deadline: selectedDate.toISOString(),
        }
      );
      if (response.status === 200) {
        message.success("Deadline updated successfully!");
        setOpenPopoverUuid(null);
        setSelectedDate(undefined);
        loadedDeadlineForUuid.current = null;
        fetchSubscribersList(); // Refresh table
      }
    } catch (error) {
      catchError(error)
    } finally {
      setUpdatingDeadline(false);
    }
  };

  useEffect(() => {
    fetchSubscribersList();
  }, [orderKey]);

  const handleDeleteButton = async (uuid) => {
    setDeletingRow(uuid);
    try {
      const res = await axiosInstance.post(
        `task/v2/task-subscribers-delete/${orderKey}/`,
        {
          uuid: [uuid],
        }
      );

      if (res?.data?.status === "success") {
        message.success("Subscriber deleted successfully");
        setDataSource((prev) => prev.filter((item) => item.uuid !== uuid));
        // fetchOrderDetails();
        fetchSubscribersList()
        setRenderStatic(true)
      } else {
        message.error("Failed to delete subscriber");
      }
    } catch (err) {
      await catchError(err);
      console.log("Error deleting subscriber:", err);
    } finally {
      setDeletingRow(null);
    }
  };

  const renderPopoverContent = (record) => {
    return (
      <div style={{ width: 250 }}>
        {/* DatePicker for Deadline */}
        <div style={{ marginBottom: 10 }}>
          <label>Deadline:</label>

          <DatePicker
            // value={selectedDate}
            value={selectedDate}
            showTime
            format="DD-MM-YYYY HH:mm A"
            style={{ width: "100%" }}
            onChange={(date) => handleDeadlineChange(date, record)}
          />
        </div>

        {/* Subscriber Type (Read-only) */}
        {/* <div style={{ marginBottom: 10 }}>
          <label>Subscriber Type:</label>
          <Input value={record.type} disabled />
        </div> */}

        {/* Subscriber Name (Read-only) */}
        {/* <div style={{ marginBottom: 10 }}>
          <label>Subscriber Name:</label>
          <Input value={record.subscriber} disabled />
        </div> */}

        {/* Save Button */}
        <Button
          loading={updatingDeadline}
          type="primary"
          onClick={() => handleUpdateDeadline(record)}
          style={{ width: "100%" }}>
          Submit
        </Button>
      </div>
    );
  };

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Name",
      dataIndex: "subscriber",
      key: "subscriber",
    },
    {
      title: "Word Count",
      dataIndex: "no_of_word",
      key: "no_of_word",
    },
    {
      title: "Completion Date",
      dataIndex: "date_of_completion",
      key: "date_of_completion",
      render: (text) => {
        return text ? moment(text).local().format("DD-MM-YYYY hh:mm A") : "N/A";
      },
    },
    {
      title: "Deadline",
      dataIndex: "deadline",
      key: "deadline",
      width: 230,
      render: (text, record) => (
        <Popover
          content={renderPopoverContent(record)}
          title="Edit Deadline"
          trigger="click"
          open={openPopoverUuid === record.uuid}
          onOpenChange={(visible) => handlePopoverOpenChange(visible, record)}>
          {text ? moment(text).local().format("DD-MM-YYYY hh:mm A") : "N/A"}{" "}
          {permissions.includes("can_edit_subscriber_deadline") && (
            <Button
              type="link"
              style={{ background: "transparent" }}
              icon={<EditOutlined />}
            />
          )}
        </Popover>
      ),
    },

    {
      title: "Author",
      dataIndex: "author",
      key: "author",
    },
    {
      title: "Created",
      dataIndex: "created",
      key: "created",
      render: (text) => {
        return text ? moment(text).local().format("DD-MM-YYYY hh:mm A") : "N/A";
      },
    },
    ...(permissions.includes("can_view_order_action_delete_subscriber")
      ? [
        {
          title: "Delete",
          dataIndex: "Delete",
          key: "Delete",
          render: (_, record) => (
            <Button
              onClick={() => record?.can_delete && handleDeleteButton(record.uuid)}
              disabled={!record?.can_delete}
              type="danger">
              {deletingRow === record.uuid ? (
                <SpinLoading />
              ) : (
                <DeleteOutlined style={{ color: record?.can_delete ? "red" : "#ccc", fontSize: "20px" }} />
              )}
            </Button>
          ),
        },
      ]
      : []),
  ];

  const totalWords = Number(orderData?.data?.writer_word_count) || 0;

  return (
    <div>
      <WordCountSummary dataSource={dataSource} totalWords={totalWords} />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "10px",
        }}>
        <Title
          level={isMobile ? 5 : 4}
          style={{ marginTop: 0, textAlign: "left" }}>
          Order SUBSCRIBER LIST - {orderKey}
        </Title>
        {permissions.includes("can_view_order_action_assign_susbcriber") && (
          <div
            onClick={() => setShowAssign(!assignSubscribe)}
            style={{
              display: "flex",
              justifyContent: "start",
              alignItems: "center",
              gap: "5px",
              cursor: "pointer",
            }}>
            {assignSubscribe === false && (
              <>
                <PlusOutlined style={{ color: "black", fontSize: "20px" }} />
                <div>Assign Subscriber</div>
              </>
            )}
          </div>
        )}
      </div>
      {loading ? (
        <SpinLoading />
      ) : (
        <>
          {assignSubscribe && (
            <AssignSubscriber
              orderData={orderData}
              setShowAssign={setShowAssign}
              // setFormSubmit={setFormSubmit}
              fetchOrderDetails={fetchOrderDetails}
              fetchSubscribersList={fetchSubscribersList}
              setRenderStatic={setRenderStatic}
            />
          )}

          {isMobile ? (
            <MobileViewComponent
              data={dataSource}
              onDelete={handleDeleteButton}
              deletingRow={deletingRow}
              renderPopoverContent={renderPopoverContent}
              permissions={permissions}
              openPopoverUuid={openPopoverUuid}
              onPopoverOpenChange={handlePopoverOpenChange}
            />
          ) : (
            <Table
              dataSource={dataSource}
              columns={columns}
              pagination={false}
              rowKey="id"
            />
          )}
        </>
      )}
    </div>
  );
};

export default ManageSubscriber;
